import { ReactNode, useEffect } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  UserCog,
  CalendarClock,
  Clock,
  Gift,
  ShoppingBag,
  BarChart3,
  ShieldAlert,
  FileText,
  Home,
  Coins,
  QrCode,
  Package,
  User,
  LogOut,
  Menu,
  X,
  Building2,
  ListOrdered,
  Heart,
  ArrowLeft,
  Settings,
  KeyRound,
  ChevronDown,
  ChevronRight,
  BookOpen,
  FolderTree,
  Tag,
  MessageSquare,
  AlertTriangle,
  PenLine,
} from 'lucide-react'
import { useState } from 'react'
import { useAuth } from '../lib/auth'
import { authApi, settingsApi, getHospitalNameFromCache } from '../lib/api'

// === 后台菜单配置：业务域一级菜单 + 具体功能二级菜单 ===
// 后台角色：admin（总后台）/ supervisor（机构负责人）/ staff（普通医护）
// 内容投稿开放后，普通医护也能进入 /admin 下的内容模块，因此菜单必须按角色裁剪，
// 否则医护会看到大量点进去就 403 的入口（详见 docs/内容投稿权限开放-规划方案.md）
type AdminRoleKey = 'admin' | 'supervisor' | 'staff'

type AdminMenuLeaf = {
  path: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  adminOnly?: boolean
  /** 仅这些角色可见；不填表示除 adminOnly 外的所有后台角色可见 */
  allowRoles?: AdminRoleKey[]
}
type AdminMenuItem =
  | (AdminMenuLeaf & { children?: undefined })
  | ({ label: string; icon: React.ComponentType<{ className?: string }>; adminOnly?: boolean; allowRoles?: AdminRoleKey[]; children: AdminMenuLeaf[] })

const ADMIN_MENU_GROUPS: AdminMenuItem[] = [
  { path: '/admin/dashboard', label: '数据看板', icon: LayoutDashboard, allowRoles: ['admin', 'supervisor'] },
  {
    label: '用户与机构',
    icon: Users,
    allowRoles: ['admin', 'supervisor'],
    children: [
      { path: '/admin/users', label: '会员管理', icon: Users },
      { path: '/admin/medical-staff', label: '医护管理', icon: UserCog },
      { path: '/admin/hospitals', label: '机构管理', icon: Building2, adminOnly: true },
    ],
  },
  {
    label: '积分运营',
    icon: Coins,
    allowRoles: ['admin', 'supervisor'],
    children: [
      { path: '/admin/events', label: '事件类型', icon: CalendarClock },
      { path: '/admin/point-entry', label: '积分录入', icon: Coins },
      { path: '/admin/point-records', label: '积分记录', icon: ListOrdered },
      { path: '/admin/gifts', label: '礼品管理', icon: Gift },
      { path: '/admin/delegated-exchange', label: '代为兑换', icon: ShoppingBag },
      { path: '/admin/exchange-records', label: '兑换记录', icon: ListOrdered },
      { path: '/admin/verify', label: '事件审核', icon: FileText },
    ],
  },
  {
    label: '内容运营',
    icon: BookOpen,
    children: [
      { path: '/admin/content/overview', label: '运营概览', icon: LayoutDashboard, adminOnly: true },
      { path: '/admin/content', label: '内容列表', icon: FileText },
      { path: '/admin/content/new', label: '投稿撰写', icon: FileText, allowRoles: ['supervisor', 'staff'] },
      { path: '/admin/content/categories', label: '分类管理', icon: FolderTree, adminOnly: true },
      { path: '/admin/content/statistics', label: '内容统计', icon: BarChart3, adminOnly: true },
      { path: '/admin/content/review-queue', label: '待审核', icon: Clock, adminOnly: true },
      { path: '/admin/content/tags', label: '标签管理', icon: Tag, adminOnly: true },
      { path: '/admin/content/comments', label: '评论管理', icon: MessageSquare, adminOnly: true },
    ],
  },
  {
    label: '数据分析',
    icon: BarChart3,
    allowRoles: ['admin', 'supervisor'],
    children: [
      { path: '/admin/statistics', label: '综合统计', icon: BarChart3 },
    ],
  },
  {
    label: '系统管理',
    icon: Settings,
    adminOnly: true,
    children: [
      { path: '/admin/settings', label: '系统设置', icon: Settings },
      { path: '/admin/risk-control', label: '风控中心', icon: ShieldAlert },
      { path: '/admin/audit-log', label: '操作日志', icon: FileText },
      { path: '/admin/sensitive-words', label: '敏感词管理', icon: AlertTriangle },
    ],
  },
]

interface LayoutProps {
  userType: 'admin' | 'medical' | 'user'
  children?: ReactNode
}

const Layout = ({ userType }: LayoutProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout, syncUser } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [passwordForm, setPasswordForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' })
  const [passwordMsg, setPasswordMsg] = useState('')
  const [maintenanceMode, setMaintenanceMode] = useState(false)
  const [maintenanceMessage, setMaintenanceMessage] = useState('')
  const [showBackToTop, setShowBackToTop] = useState(false)
  // 科普知识入口开关（后台"系统设置→功能配置"控制）
  const [knowledgeEnabled, setKnowledgeEnabled] = useState(() => {
    const saved = localStorage.getItem('knowledgeEnabled')
    return saved === null ? true : saved === 'true'
  })

  // 路由守卫：访问什么就验证什么权限，不要跨角色跳转
  useEffect(() => {
    const isSupervisor = user?.role === 'medical' && user?.medicalRole === 'supervisor'
    const isStaff = user?.role === 'medical' && user?.medicalRole === 'staff'
    // 内容投稿开放后，普通医护也能进入 /admin 下的**内容模块**（其余模块仍不可见）
    const isAdminLike = user?.role === 'admin' || isSupervisor || isStaff

    if (!user) {
      // 未登录：跳对应登录页（user端不强制跳转，放行让各页面自行处理）
      if (userType === 'admin') navigate('/admin/login', { replace: true })
      else if (userType === 'medical') navigate('/medical/login', { replace: true })
      return
    }

    if (userType === 'admin' && !isAdminLike) {
      // admin 页面但无权 → 跳 admin 登录页
      navigate('/admin/login', { replace: true })
    }
    // 机构负责人禁止访问机构管理页面
    if (isSupervisor && location.pathname === '/admin/hospitals') {
      navigate('/admin/dashboard', { replace: true })
    }
    /* 普通医护的越权拦截已**取消强制跳转**（2026-09-20 用户要求先恢复）：
       2026-09-18 引入的「非内容路径一律 replace 到 /admin/content」有两个问题 ——
         ① 它没区分端，医护端 /medical/* 也被判越权，医护一进来就被弹去投稿页，
            积分运营菜单永远看不到；
         ② 即便在自己有权限的页面（如医护端工作台），也会被反复 replace。
       现在改为「菜单按角色裁剪 + 后端 403 兜底」：看不到的入口不展示（见
       ADMIN_MENU_GROUPS 的 allowRoles / medicalMenuItems 的角色分支），
       手输越权 URL 由后端返回 403，前端不再替用户做跳转。 */
    // 登录且有权：留在当前页，不做任何跳转

    // 异步校验：访问管理后台时，确认医护角色未被注销
    if ((userType === 'admin' || userType === 'medical') && user.role === 'medical') {
      authApi.me().then((res: any) => {
        if (res && res.role !== 'medical') {
          // 已被注销 → 更新 auth_user 为普通用户角色
          const updated = { ...user, role: 'user' as const, medicalRole: undefined }
          syncUser(updated)
        }
      }).catch(() => {})
    }
  }, [user, userType, navigate, syncUser, location.pathname])

  // 初始化系统设置（每次进入页面都重新加载）
  useEffect(() => {
    const loadSystemSettings = async () => {
      try {
        const data = await settingsApi.getSystem() as any
        if (data) {
          // 拆分存储每个字段，方便单独读取
          Object.keys(data).forEach(key => {
            localStorage.setItem(key, String(data[key] ?? ''))
          })
          // 科普入口开关（未配置时默认展示）
          setKnowledgeEnabled(!(data.knowledgeEnabled === false || data.knowledgeEnabled === 'false'))
          // 检查维护模式（管理后台不受影响）
          if ((data.maintenanceMode === true || data.maintenanceMode === 'true') && userType !== 'admin') {
            setMaintenanceMode(true)
            setMaintenanceMessage(data.maintenanceMessage || '系统维护中，请稍后再试')
          } else {
            setMaintenanceMode(false)
          }
        }
      } catch (e) {
        console.error('Failed to load system settings', e)
      }
    }
    loadSystemSettings()
  }, [location.pathname])

  // 滚动显示返回顶部按钮
  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 300)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // 路径变化时自动展开包含当前路径的父菜单（手风琴：同一时间只展开一个分组）
  useEffect(() => {
    /* ⚠️ 必须用「当前端实际渲染的那份菜单」来匹配：
       原先这里固定取 adminMenuItems()，而医护端渲染的是 medicalMenuItems()。
       两者路径前缀不同（/admin/* vs /medical/*），所以医护端点进任何分组页
       （积分录入、事件列表…）都匹配不到，分组永远是收起状态 —— 表现为
       「菜单点进去不知道自己在哪」。2026-09-20 修正。 */
    const items = userType === 'medical' ? medicalMenuItems() : adminMenuItems()
    const match = items.find(item =>
      item.children && item.children.some(c =>
        location.pathname === c.path || location.pathname.startsWith(c.path + '/')
      )
    )
    setExpandedMenu(match ? match.label : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // 点击分组：展开当前分组并收回其他分组（再次点击收起自己）
  const toggleMenu = (label: string) => {
    setExpandedMenu(prev => (prev === label ? null : label))
  }

  // 当前路径最佳匹配的子菜单：最长路径优先
  // 避免访问 /admin/content/categories 时，/admin/content（内容列表）因 startsWith 误判为激活
  const bestMatchChildPath = (children: AdminMenuLeaf[]): string | null => {
    const matches = children.filter(c =>
      location.pathname === c.path || location.pathname.startsWith(c.path + '/')
    )
    if (matches.length === 0) return null
    return matches.reduce((a, b) => (b.path.length > a.path.length ? b : a)).path
  }

  /** 当前后台角色：admin / supervisor / staff；非后台角色返回 null */
  const adminRoleKey = (): AdminRoleKey | null => {
    if (user?.role === 'admin') return 'admin'
    if (user?.role === 'medical') return user?.medicalRole === 'supervisor' ? 'supervisor' : 'staff'
    return null
  }

  const adminMenuItems = (): AdminMenuItem[] => {
    const key = adminRoleKey()
    if (!key) return []
    const isAdmin = key === 'admin'
    const visible = (x: any) =>
      (isAdmin || !x.adminOnly) && (!x.allowRoles || x.allowRoles.includes(key))

    const result = ADMIN_MENU_GROUPS
      .filter(visible)
      .map(g => {
        if (!('children' in g) || !g.children) return g
        return { ...g, children: g.children.filter(visible) }
      })
      .filter(g => !('children' in g) || (g.children && g.children.length > 0))

    // 医护视角下「内容列表」实际是「我的投稿」，换成更贴切的标签
    if (!isAdmin) {
      return result.map(g =>
        ('children' in g && g.children)
          ? {
              ...g,
              children: g.children.map(c =>
                c.path === '/admin/content' ? { ...c, label: '我的投稿' } : c
              ),
            }
          : g
      ) as AdminMenuItem[]
    }
    return result
  }
  /**
   * 医护端菜单（2026-09-20 恢复并扩充）
   *
   * 历史：fca6fff（2026-05-13）把医护端统一成「工作台 + 积分录入 + 事件列表」三项，
   * 同时移除了机构负责人特殊菜单与「登录后跳管理后台」—— 结果机构负责人从这个
   * 入口进来既没有积分运营分组，也找不到审核/管理入口，表现为「积分运营菜单没了」。
   *
   * 现按角色恢复为分组结构：
   *   数据看板 + 积分运营（积分录入 / 事件列表 / 事件审核）
   *   + 用户与机构（医护管理 / 会员管理，仅机构负责人）
   *
   * ⚠️ 管理类入口只给机构负责人：后端 points.py / gifts.py 的积分记录、礼品等
   *    管理接口只授权 admin 与 supervisor，普通医护点进去必然 403。
   */
  const medicalMenuItems = (): AdminMenuItem[] => {
    const isSupervisor = user?.role === 'medical' && user?.medicalRole === 'supervisor'
    const items: AdminMenuItem[] = [
      { path: '/medical/dashboard', label: '数据看板', icon: LayoutDashboard },
      {
        label: '积分运营',
        icon: Coins,
        children: [
          { path: '/medical/point-entry', label: '积分录入', icon: Coins },
          { path: '/medical/delegated-exchange', label: '代为兑换', icon: ShoppingBag },
          { path: '/medical/events', label: '事件列表', icon: CalendarClock },
          { path: '/medical/verify', label: '事件审核', icon: FileText },
        ],
      },
      /* 图文投稿（2026-09-20 开放到医护端）
         管理端「内容运营」原本也能进，但对医护来说落在管理端布局里很别扭；
         现改为医护端独立路由 /medical/author/*，共用同一套双模页面组件。
         普通医护与机构负责人都有投稿权（后端 require_author 放行 staff / supervisor）。 */
      {
        label: '图文投稿',
        icon: BookOpen,
        children: [
          { path: '/medical/author', label: '我的投稿', icon: FileText },
          { path: '/medical/author/new', label: '投稿撰写', icon: PenLine },
        ],
      },
    ]
    if (isSupervisor) {
      items.push({
        label: '用户与机构',
        icon: Users,
        children: [
          { path: '/medical/accounts', label: '医护管理', icon: UserCog },
          { path: '/medical/users', label: '会员管理', icon: Users },
        ],
      })
    }
    return items
  }

  // 用户端底部导航：科普入口受后台"展示科普知识入口"开关控制
  const userMenuItemsAll = [
    { path: '/user/home', label: '首页', icon: Home },
    { path: '/user/activities', label: '活动', icon: Coins },
    { path: '/user/exchange', label: '兑换', icon: Package },
    { path: '/user/knowledge', label: '科普', icon: BookOpen },
    { path: '/user/profile', label: '我的', icon: User },
  ]
  const userMenuItems = knowledgeEnabled
    ? userMenuItemsAll
    : userMenuItemsAll.filter(item => item.path !== '/user/knowledge')

  const menuItems = userType === 'admin' ? adminMenuItems()
    : userType === 'medical' ? medicalMenuItems()
    : userMenuItems

  const handleLogout = () => {
    logout()
    navigate(`/${userType}/login`, { replace: true })
  }

  const handleUserTabClick = (path: string) => {
    if (path === '/user/profile' && !user) {
      navigate('/user/login')
    } else {
      navigate(path)
    }
  }

  function getTitle(): string {
    switch (userType) {
      case 'admin': {
        const map: Record<string, string> = {
          '/admin': '数据看板',
          '/admin/dashboard': '数据看板',
          '/admin/hospitals': '机构管理',
          '/admin/users': '会员管理',
          '/admin/medical-staff': '医护管理',
          '/admin/events': '事件类型',
          '/admin/point-entry': '积分录入',
          '/admin/point-records': '积分记录',
          '/admin/gifts': '礼品管理',
          '/admin/delegated-exchange': '代为兑换',
          '/admin/exchange-records': '兑换记录',
          '/admin/verify': '事件审核',
          '/admin/statistics': '综合统计',
          '/admin/risk-control': '风控中心',
          '/admin/audit-log': '操作日志',
          '/admin/settings': '系统设置',
          '/admin/content': '内容列表',
          '/admin/content/overview': '运营概览',
          '/admin/content/list': '内容列表',
          '/admin/content/new': '新建内容',
          '/admin/content/categories': '分类管理',
          '/admin/content/statistics': '内容统计',
  '/admin/content/review-queue': '待审核',
        }
        const isSupervisor = user?.role === 'medical' && user?.medicalRole === 'supervisor'
        const title = (map[location.pathname] ?? '管理后台')
        return (isSupervisor ? '机构管理后台 - ' : title + ' - ') + (localStorage.getItem('systemName') || '眉山疾控积分系统')
      }
      case 'medical': {
        const map: Record<string, string> = {
          '/medical': '数据看板',
          '/medical/dashboard': '数据看板',
          '/medical/accounts': '账号管理',
          '/medical/point-entry': '积分录入',
          '/medical/delegated-exchange': '代为兑换',
          '/medical/events': '事件列表',
          '/medical/verify': '待审核',
          '/medical/author': '我的投稿',
          '/medical/author/list': '我的投稿',
          '/medical/author/new': '投稿撰写',
        }
        // 详情页是动态路由（/medical/author/:id），精确匹配覆盖不到，按前缀兜底
        const title = map[location.pathname]
          ?? (location.pathname.startsWith('/medical/author/') ? '编辑投稿' : '医护端')
        return title + ' - ' + (localStorage.getItem('systemName') || '眉山疾控积分系统')
      }
      case 'user': {
        const map: Record<string, string> = {
          '/user': '首页',
          '/user/home': '首页',
          '/user/login': '登录',
          '/user/register': '注册',
          '/user/points': '积分明细',
          '/user/exchange': '积分兑换',
          '/user/exchange-records': '兑换记录',
          '/user/profile': '我的',
          '/user/scan': '身份码',
          '/user/activities': '积分获取',
          '/user/knowledge': '健康科普',
          '/user/account-security': '账号安全',
          '/user/notifications': '消息通知',
          '/user/personal-info': '个人资料',
        }
        // 礼品详情页特殊处理
        if (location.pathname.startsWith('/user/gift/')) return '礼品详情'
        return map[location.pathname] || localStorage.getItem('systemTitle') || '眉山健康积分'
      }
      default:
        return localStorage.getItem('systemName') || '眉山疾控积分系统'
    }
  }

  const getUserName = () => user?.name ?? '用户'

  const handlePasswordChange = async () => {
    if (!passwordForm.oldPassword) { setPasswordMsg('请输入原密码'); return }
    if (!passwordForm.newPassword) { setPasswordMsg('请输入新密码'); return }
    if (passwordForm.oldPassword === passwordForm.newPassword) { setPasswordMsg('新密码不能与原密码相同'); return }
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(passwordForm.newPassword)) {
      setPasswordMsg('密码需至少8位，包含大小写字母和数字')
      return
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) { setPasswordMsg('两次密码不一致'); return }
    setPasswordMsg('')
    try {
      await authApi.changePassword(passwordForm.oldPassword, passwordForm.newPassword)
      alert('密码修改成功！')
      setShowPasswordModal(false)
      setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '修改失败'
      setPasswordMsg(msg)
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* 顶部导航 - 用户端（固定在顶部） */}
      {userType === 'user' ? (
        <div className="bg-green-700 text-white sticky top-0 z-50 shadow-md">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => {
                if (location.pathname === '/user' || location.pathname === '/user/home') {
                  navigate('/user/home')
                } else {
                  navigate(-1)
                }
              }}
              className="p-2 hover:bg-green-600 rounded-full transition-colors"
            >
              {location.pathname === '/user' || location.pathname === '/user/home'
                ? <Home className="w-6 h-6" />
                : <ArrowLeft className="w-6 h-6" />}
            </button>
            <span className="font-bold text-lg">{getTitle()}</span>
            <Link to="/user/scan" className="p-2 hover:bg-green-600 rounded-full transition-colors">
              <QrCode className="w-6 h-6" />
            </Link>
          </div>
        </div>
      ) : (
        /* 管理端/医护端顶部导航 */
        <header className="bg-green-700 text-white shadow-lg relative z-50">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center space-x-4">
              <button
                className="lg:hidden p-2 hover:bg-green-600 rounded-lg transition-colors"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <div className="flex items-center space-x-3">
                <Heart className="w-8 h-8" />
                <div>
                  <h1 className="font-bold text-xl">
                    {userType === 'admin' ? (localStorage.getItem('systemName') || '积分管理系统') : (localStorage.getItem('systemTitle') || '眉山健康积分') + (user?.role === 'medical' && user?.medicalRole === 'supervisor' ? ' - 机构端' : ' - 医护端')}
                  </h1>
                  <p className="text-xs text-green-200">
                    {user?.role === 'medical' && user?.medicalRole === 'supervisor'
                      ? (user.hospitalName || getHospitalNameFromCache(user.hospitalId) || '机构端')
                      : userType === 'admin' ? 'MeiShan CDC Points System' : 'Medical Staff Portal'}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="hidden sm:flex items-center space-x-2 bg-green-600 hover:bg-green-500 px-4 py-2 rounded-lg transition-colors"
                >
                  <User className="w-5 h-5" />
                  <span className="font-medium">{getUserName()}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-gray-800 font-medium">{getUserName()}</p>
                      <p className="text-gray-400 text-xs mt-0.5">
                        {user?.medicalRole === 'supervisor' ? '机构负责人' : userType === 'admin' ? '管理员' : '医护人员'}
                      </p>
                    </div>
                    <button
                      onClick={() => { setShowPasswordModal(true); setUserMenuOpen(false) }}
                      className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-green-50 transition-colors"
                    >
                      <KeyRound className="w-4 h-4" />
                      <span>修改密码</span>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-3 px-4 py-3 text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>退出登录</span>
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={handleLogout}
                className="sm:hidden flex items-center space-x-2 bg-green-600 hover:bg-green-500 px-4 py-2 rounded-lg transition-colors"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </header>
      )}

      {/* ⚠️ 必须用 overflow-x-clip 而不是 overflow-x-hidden。
          `overflow-x: hidden` 会让 `overflow-y` 被规范换算成 `auto`，
          这一层就变成了一个「自己不滚动的滚动容器」——
          于是内部任何 `position: sticky` 元素的最近滚动祖先变成了它，
          页面滚动（发生在 documentElement 上）时它纹丝不动，sticky 直接失效
          （实测：内容编辑页左侧预览框滚动 600px 后 top 从 124px 变成 -430px，滑出视区）。
          `clip` 只裁切、不建立滚动容器，`overflow-y` 保持 `visible`，sticky 正常工作。
          写成两条是为了给不支持 clip 的老浏览器留 hidden 兜底（现代浏览器取后者）。 */}
      <div className="flex overflow-x-hidden overflow-x-clip">
        {/* 侧边栏 */}
        {(userType === 'admin' || userType === 'medical') && (
          <>
            {sidebarOpen && (
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
                onClick={() => setSidebarOpen(false)}
              />
            )}
            <aside
              className={`fixed lg:static inset-y-0 left-0 z-50 w-52 bg-white shadow-xl transform transition-transform duration-300 lg:translate-x-0 ${
                sidebarOpen ? 'translate-x-0' : '-translate-x-full'
              }`}
            >
              <div className="flex flex-col h-full pt-20 lg:pt-0">
                <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
                  {menuItems.map((item) => {
                    const Icon = item.icon
                    // 父菜单（带 children）
                    if (item.children) {
                      const isExpanded = expandedMenu === item.label
                      const activeChildPath = bestMatchChildPath(item.children)
                      const childActive = activeChildPath !== null
                      return (
                        <div key={item.label}>
                          <button
                            onClick={() => toggleMenu(item.label)}
                            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                              childActive
                                ? 'bg-green-50 text-green-700 font-semibold'
                                : 'text-gray-700 hover:bg-green-50 hover:text-green-700'
                            }`}
                          >
                            <span className="flex items-center space-x-3">
                              <Icon className="w-5 h-5" />
                              <span>{item.label}</span>
                            </span>
                            <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          </button>
                          {isExpanded && (
                            <div className="mt-1 ml-4 pl-4 border-l-2 border-gray-200 space-y-1">
                              {item.children.map(child => {
                                const ChildIcon = child.icon
                                const childIsActive = child.path === activeChildPath
                                return (
                                  <Link
                                    key={child.path}
                                    to={child.path}
                                    onClick={() => setSidebarOpen(false)}
                                    className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-all ${
                                      childIsActive
                                        ? 'bg-green-700 text-white shadow'
                                        : 'text-gray-600 hover:bg-green-50 hover:text-green-700'
                                    }`}
                                  >
                                    <ChildIcon className="w-4 h-4" />
                                    <span>{child.label}</span>
                                  </Link>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    }
                    // 普通菜单项
                    const isActive = location.pathname === item.path
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                          isActive
                            ? 'bg-green-700 text-white shadow-lg'
                            : 'text-gray-700 hover:bg-green-50 hover:text-green-700'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    )
                  })}
                </nav>
              </div>
            </aside>
          </>
        )}

        <main className={`flex-1 min-w-0 ${userType === 'user' ? '' : 'min-h-screen'}`}>
          <div className={(userType === 'admin' || userType === 'medical') ? 'p-6' : ''}>
            <Outlet />
          </div>
        </main>

        {/* 用户端底部导航 */}
        {userType === 'user' && (
          <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
            <div className="flex justify-around items-center py-2">
              {userMenuItems.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname === item.path ||
                  (item.path === '/user/home' && location.pathname === '/user') ||
                  (item.path === '/user/profile' && (location.pathname === '/user/login' || location.pathname === '/user/register'))
                return (
                  <div
                    key={item.path}
                    onClick={() => handleUserTabClick(item.path)}
                    className={`flex flex-col items-center py-2 px-4 transition-colors cursor-pointer ${
                      isActive ? 'text-green-700' : 'text-gray-500'
                    }`}
                  >
                    <Icon className={`w-6 h-6 ${isActive ? 'stroke-[2.5]' : ''}`} />
                    <span className="text-xs mt-1 font-medium">{item.label}</span>
                  </div>
                )
              })}
            </div>
          </nav>
        )}
      </div>

      {userType === 'user' && <div className="h-20" />}

      {/* 返回顶部按钮 */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed right-4 bottom-24 w-12 h-12 bg-green-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-green-700 transition-all z-40 ${showBackToTop ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      </button>

      {userMenuOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
      )}

      {/* 修改密码弹窗 */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-bold text-gray-800 flex items-center space-x-2">
                <KeyRound className="w-5 h-5 text-green-600" />
                <span>修改密码</span>
              </h3>
              <button
                onClick={() => { setShowPasswordModal(false); setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' }); setPasswordMsg('') }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {['oldPassword', 'newPassword', 'confirmPassword'].map((field) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {field === 'oldPassword' ? '原密码' : field === 'newPassword' ? '新密码' : '确认新密码'}
                  </label>
                  <input
                    type="password"
                    placeholder={field === 'oldPassword' ? '请输入原密码' : field === 'newPassword' ? '请输入新密码（8位以上，大小写字母+数字）' : '请再次输入新密码'}
                    value={passwordForm[field as keyof typeof passwordForm]}
                    onChange={(e) => setPasswordForm({ ...passwordForm, [field]: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              ))}
              {passwordMsg && (
                <p className="text-red-500 text-sm flex items-center space-x-1">
                  <X className="w-4 h-4" />
                  <span>{passwordMsg}</span>
                </p>
              )}
            </div>
            <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t">
              <button
                onClick={() => { setShowPasswordModal(false); setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' }); setPasswordMsg('') }}
                className="px-6 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-gray-700"
              >
                取消
              </button>
              <button
                onClick={handlePasswordChange}
                className="px-6 py-3 bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors font-medium"
              >
                确认修改
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 维护模式全屏遮罩 */}
      {maintenanceMode && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white">
          <div className="text-center px-8">
            <div className="w-20 h-20 mx-auto mb-6 bg-yellow-100 rounded-full flex items-center justify-center">
              <Settings className="w-10 h-10 text-yellow-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">系统维护中</h3>
            <p className="text-gray-600 text-lg">{maintenanceMessage}</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default Layout

